import { useState } from "react";
import { Eye, Users, Lock, Sparkles, ShieldAlert, ShieldCheck, Loader2, CreditCard } from "lucide-react";
import { toast } from "sonner";
import { errMsg } from "@/lib/api";
import { useSession } from "@/context/Session";
import { t } from "@/i18n";

export const TAG_META = {
  customer: { label: "Customer", icon: Users, cls: "chip-info" },
  team: { label: "Team", icon: Eye, cls: "chip-ok" },
  cashier: { label: "Cashier", icon: CreditCard, cls: "chip-cashier" },
  private: { label: "Private", icon: Lock, cls: "chip-muted" },
};
export const TAG_ORDER = ["customer", "team", "cashier", "private"];

export const tagsOf = (sop) => (sop?.tags?.length ? sop.tags : ["team"]);

const helpFor = (tags) => tags.includes("private") ? "Owner only. Never used in any answer, training or customer chat."
  : tags.includes("customer") && tags.includes("team") ? "Staff and customers can get answers from this."
  : tags.includes("cashier") && !tags.includes("team") ? "Only cashier roles and the owner can see this."
  : tags.includes("customer") ? "Customer chat only. Not used for staff answers or training."
  : "Staff only. Used for staff answers and training, never for customers.";

export const TagChips = ({ tags, testid }) => (
  <span className="inline-flex flex-wrap gap-1.5" data-testid={testid}>
    {TAG_ORDER.filter((k) => (tags?.length ? tags : ["team"]).includes(k)).map((k) => {
      const { icon: Icon, cls, label } = TAG_META[k];
      return <span key={k} className={`chip ${cls}`} data-testid={testid ? `${testid}-${k}` : undefined}><Icon size={11} /> {t(label)}</span>;
    })}
  </span>
);

export const toggleTag = (tags, k) => {
  if (k === "private") return ["private"];
  const base = tags.filter((x) => x !== "private");
  const next = base.includes(k) ? base.filter((x) => x !== k) : [...base, k];
  return next.length ? TAG_ORDER.filter((x) => next.includes(x)) : null;
};

const sameTags = (a, b) => [...a].sort().join() === [...b].sort().join();

function CustomerStatus({ sop, status, busy, onOverride }) {
  return (
    <div className={`banner ${status === "held" ? "banner-warn" : "banner-info"}`} data-testid={`customer-status-${status}`}>
      {status === "held" ? <ShieldAlert size={18} /> : status === "live" ? <ShieldCheck size={18} /> : <Loader2 size={18} className="animate-spin" />}
      <div className="stack-sm">
        <b>{t({ held: "Held back from customers", live: "Live for customers", checking: "Checking for internal details…", draft: "Goes live for customers after publishing" }[status])}</b>
        {status === "held" && !!sop.customer_check?.sensitive_items?.length && <p className="text-sm">{sop.customer_check.sensitive_items.join(" · ")}</p>}
        {status === "held" && <button className="btn btn-sm btn-ghost self-start" disabled={!!busy} onClick={onOverride} data-testid="customer-override-button">{t("I've checked it")}</button>}
      </div>
    </div>
  );
}

export function AudiencePanel({ sop, api, reload }) {
  const { me } = useSession();
  const [busy, setBusy] = useState("");
  const tags = tagsOf(sop);
  const sug = sop.ai_suggestion;
  const run = async (key, fn, msg) => {
    setBusy(key);
    try { await fn(); if (msg) toast.success(t(msg)); await reload(); } catch (e) { toast.error(errMsg(e)); }
    setBusy("");
  };
  const save = (next, override = false) => run(next.join() + override, () => api.patch(`/sops/${sop.id}/tags`, { tags: next, override }), "Saved");
  const toggle = (k) => {
    const next = toggleTag(tags, k);
    if (!next) return toast.error(t("Keep at least one tag."));
    if (!sameTags(next, tags)) save(next);
  };
  const status = !tags.includes("customer") ? null : sop.customer_hold ? "held" : sop.customer_ok ? "live" : sop.published_version ? "checking" : "draft";
  return (
    <div className="card stack-sm" data-testid="audience-panel">
      <p className="mono-label">{t("Who can see this")}</p>
      <div className="flex flex-wrap gap-2">
        {TAG_ORDER.filter((k) => k !== "private" || me.role === "admin").map((k) => {
          const { icon: Icon, label } = TAG_META[k];
          const on = tags.includes(k);
          return <button key={k} className={`btn btn-sm ${on ? "btn-primary" : "btn-ghost"}`} aria-pressed={on} disabled={!!busy} onClick={() => toggle(k)} data-testid={`tag-toggle-${k}`}><Icon size={14} /> {t(label)}</button>;
        })}
      </div>
      <p className="text-xs faint">{t("Customer, Team and Cashier can be combined. Private is owner-only and can't be combined.")}</p>
      <p className="text-sm muted flex flex-wrap items-center gap-2"><TagChips tags={tags} testid="audience-current-tags" /> {t(helpFor(tags))}</p>
      {status && <CustomerStatus sop={sop} status={status} busy={busy} onOverride={() => save(tags, true)} />}
      <div className="stack-sm border-t border-[var(--border-color)] pt-3">
        <div className="flex items-center justify-between gap-2">
          <p className="text-sm font-semibold flex items-center gap-2"><Sparkles size={14} className="text-[var(--accent-primary)]" /> {t("AI tag check")}</p>
          <button className="btn btn-sm btn-ghost" disabled={!!busy} onClick={() => run("suggest", () => api.post(`/sops/${sop.id}/suggest-tag`))} data-testid="audience-suggest-button">
            {busy === "suggest" ? <Loader2 size={14} className="animate-spin" /> : null} {t(sug ? "Check again" : "Suggest a tag")}
          </button>
        </div>
        {sug && (
          <div className="stack-sm" data-testid="audience-suggestion">
            <p className="text-sm flex flex-wrap items-center gap-2"><TagChips tags={sug.tags} testid="audience-suggested-tags" /> {sug.reason}</p>
            {!!sug.sensitive_items?.length && <p className="text-xs muted" data-testid="audience-sensitive">{t("Sensitive")}: {sug.sensitive_items.join(" · ")}</p>}
            {sop.too_open && <p className="text-xs text-[var(--status-error)]" data-testid="audience-too-open">{t("This tag may be too open for what's written.")}</p>}
            <div className="flex gap-2">
              {!sameTags(sug.tags || ["team"], tags) && (!(sug.tags || []).includes("private") || me.role === "admin") &&
                <button className="btn btn-sm btn-primary" disabled={!!busy} onClick={() => save(sug.tags)} data-testid="audience-use-suggestion">{t("Use these tags")}</button>}
              <button className="btn btn-sm btn-ghost" onClick={() => run("dismiss", () => api.post(`/sops/${sop.id}/dismiss-tag`))} data-testid="audience-dismiss">{t("Dismiss")}</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
